import { Language } from '../lang'
import { logger } from './Logger'

interface CryptoProvider {
  /** ignores environment loaded crypto */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ignoreCrypto(): any
  /**
   * implementation of randomBytes
   */
  randomBytes(): Buffer
  /**
   * implementation of randomInt
   * @param max size of value to generate
   */
  randomInt(max: number): number
}

/**
 * Default Crypto Provider with Environment Inspection.
 *
 * Follows the approach used by `crypto-js`
 * [here](https://github.com/brix/crypto-js/blob/develop/src/core.js)
 */
class DefaultCryptoProvider implements CryptoProvider {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private cryptoProvider: any

  constructor() {
    this.loadCryptoFromEnvironment()
  }
  /**
   * Returns random bytes using secure random if possible. If no crypto
   * module is available, Math.random and the unsigned right shift operator
   * will be used to generate a random Uint32. As a note `4294967295` is the
   * max value of a Uint32.
   *
   * @returns
   */
  randomBytes(): Buffer {
    if (this.cryptoProvider) {
      if (typeof this.cryptoProvider.randomBytes === 'function') {
        try {
          return <Buffer>this.cryptoProvider.randomBytes(Uint32Array.BYTES_PER_ELEMENT)
        } catch (err) {
          /** Ignored */
        }
      }
    }

    logger.warn(Language.strings.crypto.CryptoNotAvailableUsingMath)
    const randomBytes = Buffer.alloc(31 * Uint32Array.BYTES_PER_ELEMENT, Math.floor(Math.random() * 4294967295) >>> 0)
    return randomBytes
  }
  /**
   * returns a secure random int if possible from randomBytes
   *
   * @param max value to generate less than max
   */
  randomInt(max: number): number {
    return Math.floor((this.randomBytes().readUInt32LE() / 0xffffffff) * max)
  }
  /**
   * Ignores the environment loaded crypto provider
   */
  ignoreCrypto() {
    this.cryptoProvider = undefined
  }
  /**
   * loads the crypto module by inspecting the environment
   */
  private loadCryptoFromEnvironment() {
    if (!this.cryptoProvider && typeof global !== 'undefined' && global.crypto) {
      this.cryptoProvider = global.crypto
    }

    if (!this.cryptoProvider && typeof require === 'function') {
      try {
        this.cryptoProvider = require('node:crypto')
      } catch (error) {
        /* Ignored */
      }
    }

    if (!this.cryptoProvider) {
      logger.warn(Language.strings.crypto.CryptoNotAvailable)
    }
  }
}

export { type CryptoProvider, DefaultCryptoProvider }
